/* Query and mutation hooks for Venues — STORY-013.
 *
 * The list is read by two screens: the Venues destination, and the picker on an
 * Event. One query key, so creating a Venue inline while writing an Event makes
 * it appear in the destination too without either screen knowing about the
 * other.
 */

import {
  type CreateVenueRequest,
  type UpdateVenueRequest,
  venueSchema,
  venuesResponseSchema,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';
import { eventsKey } from '../../events/index.js';

export const venuesKey = (organisationId: string) =>
  ['organisations', organisationId, 'venues'] as const;

export function useVenues(organisationId: string) {
  return useQuery({
    queryKey: venuesKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/venues`, { schema: venuesResponseSchema }),
    retry: false,
  });
}

export function useCreateVenue(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateVenueRequest) =>
      request(`/organisations/${organisationId}/venues`, {
        method: 'POST',
        schema: venueSchema,
        body: values,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: venuesKey(organisationId) });
    },
  });
}

export function useUpdateVenue(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ venueId, ...values }: UpdateVenueRequest & { venueId: string }) =>
      request(`/organisations/${organisationId}/venues/${venueId}`, {
        method: 'PATCH',
        schema: venueSchema,
        body: values,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: venuesKey(organisationId) });
      /* Renaming a Venue changes what every Event referencing it displays —
       * that is the point of the record — so the Events have to be re-read. */
      await queryClient.invalidateQueries({ queryKey: eventsKey(organisationId) });
    },
  });
}

export function useDeleteVenue(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (venueId: string) =>
      request(`/organisations/${organisationId}/venues/${venueId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: venuesKey(organisationId) });
    },
  });
}
